/**
 * <hydrate-store-list> — Layer 3 consumer custom element.
 *
 * Renders store cards built from the APIM store-search result produced by
 * the hydrateStoreList pipeline. Unlike a server-rendered Shopify page,
 * these cards come entirely from the NG API response (catalogName, catalogLogo,
 * wwwLink, etc.) — no Liquid loop is involved. Card markup is delegated to
 * `renderStoreCard` from @theme/ng-store-card so the icon-list layout stays
 * consistent across the SLP rail, store list page, and partner stores.
 *
 * Architecture position:
 *   Layer 1: query-store-list.js    (raw APIM fetch)
 *   Layer 2: hydrate-callers.js     (orchestration + `theme:store-list:hydrated` dispatch)
 *   Layer 3: hydrate-store-list.js  (this module — renders cards into its own section)
 *
 * Data source:
 *   - `window.__ngStoreListHydration` — the cached result, set by the auto-bootstrap
 *     in snippets/scripts.liquid once hydrateStoreList resolves (race-condition guard).
 *   - `theme:store-list:hydrated` CustomEvent on `document` — dispatched by
 *     hydrate-callers.js for live updates.
 */

import { renderStoreCard, renderStoreCardSkeletons, initStoreCardInteractions } from '@theme/ng-store-card';

/**
 * @typedef {Object} StoreHit
 * @property {string} [catalogId]
 * @property {string} [catalogName]
 * @property {string} [catalogLogo]
 * @property {string} [wwwLink]
 * @property {string} [name]
 * @property {{ title?: string, storePageUrl?: string, media?: string }} [derived]
 * @property {Object} [labels]
 * @property {Object} [rewards]
 */

class HydrateStoreList extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onHydrated = null;

  connectedCallback() {
    this.maxStores = parseInt(this.getAttribute('data-max-stores') || '25', 10);

    this.#onHydrated = (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.#render(detail);
    };
    document.addEventListener('theme:store-list:hydrated', this.#onHydrated);

    // Race guard: if hydrateStoreList resolved before this element connected,
    // the event has already fired — process the cached result now. Otherwise a
    // fetch is in flight: paint skeletons until it resolves (the theme editor
    // has no APIM data, so keep the placeholder there instead).
    if (window.__ngStoreListHydration) {
      this.#render(window.__ngStoreListHydration);
    } else if (this.#isDesignMode) {
      this.#showEmptyState();
    } else {
      this.#showLoading();
    }
  }

  disconnectedCallback() {
    if (this.#onHydrated) {
      document.removeEventListener('theme:store-list:hydrated', this.#onHydrated);
    }
  }

  get #grid() {
    return this.querySelector('[data-hsl-grid]');
  }

  get #status() {
    return this.querySelector('[data-hsl-status]');
  }

  /** Whether the storefront is being viewed in the Shopify theme editor. */
  get #isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /**
   * Renders store cards from a hydration result. Only acts on store-list
   * context results.
   *
   * @param {{ context?: string, stores?: { ok?: boolean, data?: { stores?: StoreHit[], totalCount?: number }, error?: { message?: string } } } | null | undefined} result
   */
  #render(result) {
    if (!result || result.context !== 'store-list') return;

    const envelope = result.stores;

    if (!envelope || !envelope.ok) {
      this.#showEmptyState(envelope?.error?.message);
      return;
    }

    const hits = Array.isArray(envelope.data?.stores) ? envelope.data.stores : [];

    if (hits.length === 0) {
      this.#showEmptyState('No stores to display.');
      return;
    }

    const grid = this.#grid;
    if (!grid) return;

    grid.innerHTML = hits
      .slice(0, this.maxStores)
      .map((hit) => renderStoreCard(hit))
      .join('');

    grid.removeAttribute('aria-busy');
    initStoreCardInteractions(grid);

    const status = this.#status;
    if (status) status.hidden = true;

    this.hidden = false;
  }

  /**
   * Paints store-card skeletons into the grid while the store list is loading,
   * keeping the section visible and marking the grid `aria-busy`. Replaced by
   * real cards (or hidden) once the fetch resolves.
   */
  #showLoading() {
    const grid = this.#grid;
    if (!grid) return;

    this.hidden = false;

    const status = this.#status;
    if (status) status.hidden = true;

    const count = Math.min(this.maxStores || 8, 12);
    grid.setAttribute('aria-busy', 'true');
    grid.innerHTML = renderStoreCardSkeletons(count);
  }

  /**
   * Hides the whole section on the live storefront when there is nothing to
   * show. In the theme editor the section stays visible with a placeholder.
   *
   * @param {string} [message]
   */
  #showEmptyState(message) {
    if (this.#isDesignMode) {
      this.hidden = false;
      const status = this.#status;
      if (status) {
        status.hidden = false;
        status.textContent = message || 'Store results will appear here when data is available.';
      }
      return;
    }

    this.hidden = true;
  }

}

if (!customElements.get('hydrate-store-list')) {
  customElements.define('hydrate-store-list', HydrateStoreList);
}
